import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  FaTachometerAlt, FaExclamationTriangle, FaFileMedicalAlt, FaTable, FaTasks,
  FaUsers, FaUserShield, FaSignOutAlt, FaBars, FaUserCircle, FaTimes, FaChevronRight,
  FaChalkboardTeacher, FaHardHat, FaClipboardCheck, FaChartLine, FaQrcode, FaCog,
  FaHeartbeat, FaVial, FaClipboardList, FaUsersCog, FaBell,
  FaFileContract, FaCalendarAlt,
} from 'react-icons/fa';
import api from '../config/axios';
import BrandLogo from './BrandLogo';
import './Layout.css';

const Layout: React.FC = () => {
  const { user, logout, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarPinned, setSidebarPinned] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [alertCounts, setAlertCounts] = useState({ alta: 0, media: 0, baja: 0, total: 0 });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (sidebarOpen && isMobile) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [sidebarOpen, isMobile]);

  // Centro de alertas: consulta el conteo una vez al montar (usuario autenticado).
  // Error silencioso → badge 0.
  useEffect(() => {
    if (!isAuthenticated) return;
    api.get('/alerts')
      .then((r) => setAlertCounts(r.data.counts || { alta: 0, media: 0, baja: 0, total: 0 }))
      .catch(() => {});
  }, [isAuthenticated]);

  // Rutas protegidas: sin sesión → login.
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const handleLogout = () => { logout(); navigate('/login'); };
  const closeSidebar = () => setSidebarOpen(false);
  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const isAdmin = user?.role === 'administrador';
  const isSupervisor = user?.role === 'supervisor';

  const navItems = [
    { path: '/', icon: <FaTachometerAlt />, label: 'Dashboard' },
    { path: '/reportes', icon: <FaExclamationTriangle />, label: 'Reportes' },
    { path: '/incidentes', icon: <FaFileMedicalAlt />, label: 'Incidentes' },
    { path: '/iperc', icon: <FaTable />, label: 'IPERC' },
    { path: '/capacitaciones', icon: <FaChalkboardTeacher />, label: 'Capacitaciones' },
    { path: '/epp', icon: <FaHardHat />, label: 'EPP' },
    { path: '/emo', icon: <FaHeartbeat />, label: 'Exámenes médicos' },
    { path: '/monitoreo', icon: <FaVial />, label: 'Monitoreo agentes' },
    { path: '/inspecciones', icon: <FaClipboardCheck />, label: 'Inspecciones' },
    { path: '/auditorias', icon: <FaClipboardList />, label: 'Auditorías' },
    { path: '/comite', icon: <FaUsersCog />, label: 'Comité SST' },
    { path: '/documentos', icon: <FaFileContract />, label: 'Documentos' },
    { path: '/plan-anual', icon: <FaCalendarAlt />, label: 'Plan Anual' },
    { path: '/alertas', icon: <FaBell />, label: 'Alertas' },
    { path: '/acciones', icon: <FaTasks />, label: 'Acciones' },
    { path: '/estadisticas', icon: <FaChartLine />, label: 'Estadísticas' },
    { path: '/qr', icon: <FaQrcode />, label: 'Código QR' },
    ...((isAdmin || isSupervisor) ? [{ path: '/usuarios', icon: <FaUsers />, label: 'Usuarios' }] : []),
    ...(isAdmin ? [{ path: '/configuracion', icon: <FaCog />, label: 'Configuración' }] : []),
    ...(user?.isSuperAdmin ? [{ path: '/super-admin', icon: <FaUserShield />, label: 'Super-Admin' }] : []),
  ];

  const roleBadge = user?.role === 'administrador' ? { bg: '#F59E0B', label: 'Admin' }
    : user?.role === 'supervisor' ? { bg: '#1976A6', label: 'Supervisor' }
    : { bg: '#16a34a', label: 'Trabajador' };

  const alertBadgeColor = alertCounts.alta > 0 ? '#dc2626' : alertCounts.media > 0 ? '#f59e0b' : '#94a3b8';

  return (
    <div className="layout-container">
      {isMobile && (
        <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Menu">
          <FaBars />
        </button>
      )}

      {/* Campana de alertas / vencimientos (topbar flotante) */}
      <button
        onClick={() => navigate('/alertas')}
        title="Alertas y vencimientos"
        aria-label="Alertas y vencimientos"
        style={{
          position: 'fixed', top: 14, right: 16, zIndex: 15000,
          width: 42, height: 42, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: '#fff', color: '#334155', boxShadow: '0 2px 10px rgba(15,23,42,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17,
        }}
      >
        <FaBell />
        {alertCounts.total > 0 && (
          <span style={{
            position: 'absolute', top: -3, right: -3, minWidth: 20, height: 20, padding: '0 5px',
            borderRadius: 10, background: alertBadgeColor, color: '#fff', fontSize: 11, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff',
          }}>
            {alertCounts.total > 99 ? '99+' : alertCounts.total}
          </span>
        )}
      </button>

      {isMobile && sidebarOpen && <div className="sidebar-overlay open" onClick={closeSidebar} />}

      <aside
        className={`sidebar ${isMobile ? (sidebarOpen ? 'mobile-open' : 'mobile-closed') : (sidebarPinned ? 'expanded' : '')}`}
        onMouseEnter={() => !isMobile && setSidebarPinned(true)}
        onMouseLeave={() => !isMobile && setSidebarPinned(false)}
      >
        <div className="sidebar-header">
          <BrandLogo size={34} light />
          {isMobile && <button className="sidebar-close" onClick={closeSidebar}><FaTimes /></button>}
        </div>

        <div className="sidebar-user">
          <div className="sidebar-avatar"><FaUserCircle /></div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{user?.name || 'Usuario'}</div>
            <span className="sidebar-role-badge" style={{ background: roleBadge.bg }}>{roleBadge.label}</span>
            {user?.tenantName && <div className="sidebar-tenant">{user.tenantName}</div>}
          </div>
        </div>

        <div className="sidebar-divider" />

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={closeSidebar}
              title={item.label}
            >
              <span className="sidebar-nav-icon">{item.icon}</span>
              <span className="sidebar-nav-label">{item.label}</span>
              {isActive(item.path) && <FaChevronRight className="sidebar-nav-indicator" style={{ fontSize: 10, opacity: 0.7 }} />}
            </Link>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button className="sidebar-nav-item logout" onClick={handleLogout} title="Cerrar sesión">
            <span className="sidebar-nav-icon"><FaSignOutAlt /></span>
            <span className="sidebar-nav-label">Salir</span>
          </button>
          <div className="sidebar-version">Previx v{process.env.REACT_APP_VERSION || '1.0.0'}</div>
        </div>
      </aside>

      <main className={`main-content ${!isMobile && sidebarPinned ? 'sidebar-expanded' : ''}`}>
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
